import { useEffect, useRef, useState } from 'react';

import { personNameField } from '@/lib/validation/fields';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { X } from 'lucide-react';

const fullNameSchema = personNameField('Nom', 100);

interface ContactModalProps {
  recipientName: string;
  senderName: string;
  isPending: boolean;
  onSend: (data: { fullName: string; message: string; hcaptchaToken: string }) => void;
  onClose: () => void;
}

export function ContactModal({ recipientName, senderName, isPending, onSend, onClose }: ContactModalProps) {
  const [fullName, setFullName] = useState(senderName);
  const [message, setMessage] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const siteKey = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!captchaToken) return;
    const parsed = fullNameSchema.safeParse(fullName);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message ?? 'Nom invalide');
      return;
    }
    setNameError(null);
    onSend({ fullName: parsed.data, message, hcaptchaToken: captchaToken });
  }

  const canSubmit = fullName.trim() && message.trim() && captchaToken && !isPending;

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby='contact-modal-title'
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
      tabIndex={-1}
    >
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />
      <div className='relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6 flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <h2 id='contact-modal-title' className='text-lg font-bold text-slate-900'>
            Contacter {recipientName}
          </h2>
          <button
            type='button'
            onClick={onClose}
            disabled={isPending}
            className='text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='flex flex-col gap-3'>
          <div className='flex flex-col gap-1'>
            <label htmlFor='contact-fullname' className='text-sm font-medium text-slate-700'>
              Votre nom
            </label>
            <input
              id='contact-fullname'
              type='text'
              value={fullName}
              onChange={e => {
                setFullName(e.target.value);
                if (nameError) setNameError(null);
              }}
              required
              maxLength={100}
              disabled={isPending}
              className='border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-40'
            />
            {nameError && <p className='text-xs text-red-600 font-medium'>{nameError}</p>}
          </div>

          <div className='flex flex-col gap-1'>
            <label htmlFor='contact-message' className='text-sm font-medium text-slate-700'>
              Message
            </label>
            <textarea
              id='contact-message'
              value={message}
              onChange={e => setMessage(e.target.value)}
              required
              maxLength={2000}
              rows={4}
              disabled={isPending}
              className='border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-40 resize-none'
            />
            <span className='text-xs text-slate-400 text-right'>{message.length}/2000</span>
          </div>

          <HCaptcha
            ref={captchaRef}
            sitekey={siteKey}
            onVerify={token => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
          />

          <div className='flex gap-3 justify-end pt-1'>
            <button
              type='button'
              onClick={onClose}
              disabled={isPending}
              className='px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40'
            >
              Annuler
            </button>
            <button
              type='submit'
              disabled={!canSubmit}
              className='px-4 py-2 text-sm rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors disabled:opacity-40'
            >
              {isPending ? 'Envoi...' : 'Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
